import { useEffect, useRef, useState } from 'react';

/**
 * In-session voice control via the self-owned "Hey VERA" wake word
 * (livekit-wakeword model, 3-stage ONNX pipeline, 100% in-browser). This
 * REPLACES Picovoice (Porcupine + Rhino) — no vendor SDK, no access key, no
 * per-use cost.
 *
 * Flow: while a session is active the detector listens for "Hey VERA" and fires
 * `onWakeWord` on each detection. The consumer decides what that does — today it
 * toggles Privacy Mode (say "Hey VERA" to pause, say it again to resume).
 *
 * The engine is created ONLY while a session is active and fully torn down (mic
 * released) when it ends or the component unmounts.
 *
 * Model files live in /public/models/ and are served at /models/:
 *   - hey_vera.onnx          (trained classifier)
 *   - melspectrogram.onnx    (shared front-end)
 *   - embedding_model.onnx   (shared front-end)
 * The onnxruntime-web WASM binaries are vendored at /public/ort/ (offline-safe).
 *
 * Feature flag: set REACT_APP_WAKEWORD_ENABLED=false to disable (rollback).
 * Threshold: REACT_APP_WAKEWORD_THRESHOLD overrides the default 0.36 (the
 * eval-optimized value from the training run).
 *
 * NOTE (Create React App): only REACT_APP_* env vars are inlined into the bundle.
 */

const PUBLIC = process.env.PUBLIC_URL || '';
const MODEL_URL = `${PUBLIC}/models/hey_vera.onnx`;
const MEL_URL = `${PUBLIC}/models/melspectrogram.onnx`;
const EMB_URL = `${PUBLIC}/models/embedding_model.onnx`;
const ORT_WASM_PATHS = `${PUBLIC}/ort/`;

const ENABLED = process.env.REACT_APP_WAKEWORD_ENABLED !== 'false';
const THRESHOLD = Number(process.env.REACT_APP_WAKEWORD_THRESHOLD) || 0.36;

// Verbose diagnostics — grep the browser console for "[VoiceCommands]".
const LOG = (...args: any[]) => console.log('[VoiceCommands]', ...args);
const LOGERR = (...args: any[]) => console.error('[VoiceCommands]', ...args);

export type VoiceStatus =
  | 'idle' // not active (no session / disabled)
  | 'loading' // model loading
  | 'listening' // waiting for "Hey VERA"
  | 'denied' // microphone permission denied
  | 'error'; // failed to load

export interface UseVoiceCommandsArgs {
  isSessionActive: boolean;
  /** Fired on each "Hey VERA" detection. Consumer decides the action. */
  onWakeWord: () => void;
}

export interface UseVoiceCommandsResult {
  status: VoiceStatus;
  error: string | null;
  /** Latest model score (0..1), ~10 Hz — for an optional UI meter. */
  score: number;
}

export function useVoiceCommands({
  isSessionActive,
  onWakeWord,
}: UseVoiceCommandsArgs): UseVoiceCommandsResult {
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [score, setScore] = useState(0);

  // Keep the latest callback without re-initializing the engine (so the
  // consumer's toggle always sees fresh state).
  const onWakeWordRef = useRef(onWakeWord);
  onWakeWordRef.current = onWakeWord;

  useEffect(() => {
    if (!isSessionActive) {
      setStatus('idle');
      return;
    }
    if (!ENABLED) {
      LOG('wake word disabled via REACT_APP_WAKEWORD_ENABLED=false');
      setStatus('idle');
      return;
    }

    let disposed = false;
    // Detector is created lazily (dynamic import keeps onnxruntime-web out of the
    // main bundle); hold it here so cleanup can stop it even mid-load.
    let detector: { stop: () => Promise<void> } | null = null;

    (async () => {
      setStatus('loading');
      setError(null);
      setScore(0);
      LOG('loading "Hey VERA" detector — threshold', THRESHOLD);
      try {
        const { WakeWordDetector } = await import('../services/WakeWordDetector');
        if (disposed) return;
        const det = new WakeWordDetector({
          modelUrl: MODEL_URL,
          melspectrogramUrl: MEL_URL,
          embeddingUrl: EMB_URL,
          ortWasmPaths: ORT_WASM_PATHS,
          threshold: THRESHOLD,
          onDetected: ({ score: s }) => {
            LOG('WAKE WORD DETECTED — score', s.toFixed(3));
            onWakeWordRef.current();
          },
          onScore: (s) => {
            if (!disposed) setScore(s);
          },
        });
        // start() prompts the mic permission; a denial rejects here.
        await det.start();
        if (disposed) {
          void det.stop();
          return;
        }
        detector = det;
        setStatus('listening');
        LOG('LISTENING — say "Hey VERA"');
      } catch (e: any) {
        LOGERR('initialization failed:', e);
        if (disposed) return;
        const msg = String(e?.message || e || '');
        if (/denied|permission|notallowed|not allowed/i.test(msg)) {
          setStatus('denied');
          setError('Microphone access is needed for voice commands. Use the on-screen button instead.');
        } else {
          setStatus('error');
          setError(msg || 'Voice commands could not start.');
        }
      }
    })();

    return () => {
      disposed = true;
      LOG('deactivating — releasing detector + microphone');
      if (detector) {
        void detector.stop();
        detector = null;
      }
    };
  }, [isSessionActive]);

  return { status, error, score };
}

export default useVoiceCommands;
